/**
 * Convert pasted HTML to a markdown-flavored plain-text string that preserves
 * hyperlinks as `[text](url)`. Designed for the agent prompt textarea, which
 * otherwise loses the URL portion of any pasted hyperlink.
 *
 * Returns `null` when there is nothing to convert (no HTML or no anchors), so
 * the caller can fall through to the native paste behaviour.
 */

const BLOCK_DOUBLE_BREAK = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const BLOCK_SINGLE_BREAK = new Set(['DIV', 'BR', 'LI', 'TR']);
const SAFE_SCHEME = /^(https?:|mailto:)/i;
const URL_NEEDS_ANGLES = /[\s<>]/;

const escapeLinkText = (text) =>
  text.replaceAll(/[\\[\]]/g, String.raw`\$&`);

const formatUrl = (href) =>
  URL_NEEDS_ANGLES.test(href) ? `<${href}>` : href;

const walk = (node, out) => {
  if (node.nodeType === Node.TEXT_NODE) {
    out.push(node.textContent);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  if (node.tagName === 'A' && node.hasAttribute('href')) {
    const rawHref = node.getAttribute('href').trim();
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (SAFE_SCHEME.test(rawHref)) {
      out.push(text ? `[${escapeLinkText(text)}](${formatUrl(rawHref)})` : rawHref);
    } else {
      // Unsafe scheme (e.g. javascript:, data:) — drop URL, keep visible text.
      out.push(text);
    }
    return;
  }

  for (const child of node.childNodes) walk(child, out);
  if (BLOCK_DOUBLE_BREAK.has(node.tagName)) out.push('\n\n');
  else if (BLOCK_SINGLE_BREAK.has(node.tagName)) out.push('\n');
};

export const htmlToMarkdownLinks = (html) => {
  if (!html?.includes('<a')) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  if (!doc.body.querySelector('a[href]')) return null;
  const out = [];
  walk(doc.body, out);
  return out
    .join('')
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
